/**
 * moneyComponentLedger.js — row-level "מה הצבע של כל שקל" ledger, per fund.
 *
 * מכליל את הסיווג ב-honKitzba.js לרמת שורה (perPeriod entry) במקום סיכומי-קרן
 * בלבד, כדי שאפשר יהיה לקבץ צבע-כסף גם לפי מעסיק על פני כל הקרנות של לקוח.
 * honKitzba.js משתמש במודול הזה כדי לבנות את הסיכומים שלו — כל הסיווג נשאר
 * במקום אחד.
 *
 * הערה: employer נלקח מ-perPeriod row (p.maasikName, מקור MPR-MAASIK-BE-YATZRAN
 * ברמת BlockItrot — אותו שדה ש-BreakdownTab.jsx ו-depositsTable.js כבר מציגים),
 * לא מ-fund.employer (מעסיק יחיד קבוע לכל הקרן). לקרן יכולים להיות כמה BlockItrot
 * עם מעסיקים שונים (למשל שינוי שם חברה / רצף בין מעסיקים) — fund.employer
 * משמש רק כ-fallback לשורות בלי maasikName (למשל קרן השתלמות שאין לה perPeriod).
 */

const TAGMULIM_REKIV = new Set(["2", "3", "4", "10"]);

// קטגוריות שהן הון מלא (fund.redeem ישירות, אין פיצול perPeriod) — כל אחת
// מקבלת color משלה (שם הקטגוריה עצמה) כי לכל אחת טיפול מס שונה לגמרי:
// קה"ש פטורה אחרי נזילות, קופ"ג להשקעה היא השקעה פרטית ללא הטבה,
// חיסכון לכל ילד פטור לפי משיכה בגיל 21.
const CAPITAL_CAT_COLOR = {
  "קרן השתלמות":     'קה"ש',
  "קופת גמל להשקעה": "קופת גמל להשקעה",
  "חיסכון לכל ילד":   "חיסכון לכל ילד",
};

/**
 * @param {object} fund — parsed fund object from xmlParser
 * @returns {Array<{fundId, employer, rekiv, sugItra, shichva, amount, color}>}
 */
export function buildFundLedger(fund) {
  const fundId  = fund.id || `${fund.companyCode || ""}_${fund.polNum || ""}`;
  const fundEmployer = fund.employer?.name || null;

  if (CAPITAL_CAT_COLOR[fund.cat]) {
    const amount = fund.redeem || 0;
    if (!amount) return [];
    return [{
      fundId, employer: fundEmployer, rekiv: null, sugItra: null, shichva: null, amount,
      color: CAPITAL_CAT_COLOR[fund.cat],
    }];
  }

  const perPeriod = fund.yitrot?.perPeriod || [];
  const rows = [];

  perPeriod.forEach(p => {
    const amount = p.amount || 0;
    if (!amount) return;
    const employer = p.maasikName || fundEmployer;
    const base = { fundId, employer, rekiv: p.rekiv, sugItra: p.sugItra, shichva: p.shichva, amount };

    if (TAGMULIM_REKIV.has(p.rekiv)) {
      if (p.rekiv === "10") {
        rows.push({ ...base, color: "תיקון 190" });
      } else if (p.sugItra === "1") {
        rows.push({ ...base, color: "תגמולים הון" });
      } else if (p.sugItra === "2" || p.sugItra === "3") {
        rows.push({ ...base, color: "תגמולים קצבה" });
      } else if (p.sugItra === "4") {
        rows.push({ ...base, color: "נתון לבחירה" });
      }
    } else if (p.rekiv === "1") {
      if (p.sugItra === "1") rows.push({ ...base, color: "פיצויים הון" });
      else if (p.sugItra === "2" || p.sugItra === "3") rows.push({ ...base, color: "פיצויים קצבה" });
      else if (p.sugItra === "4") rows.push({ ...base, color: "נתון לבחירה (פיצויים)" });
    }
  });

  // Fallback: fund header says tikun190 but perPeriod never used rekiv="10" —
  // clearinghouse inconsistency seen in קופות גמל, so it applies ONLY there.
  // כספי תיקון 190 יושבים בקופת גמל; יצרנים (למשל הפניקס) מדווחים לעיתים
  // TIKUN-190=1 גם על קרן פנסיה מקיפה — שם הכספים הם קצבה לפי דין, ואסור
  // להפוך אותם להון. שורות מפורשות rekiv="10" נצבעות "תיקון 190" בכל מוצר.
  const kitzbaSum = rows.filter(r => r.color === "תגמולים קצבה").reduce((s, r) => s + r.amount, 0);
  const hasHon = rows.some(r => r.color === "תגמולים הון" || r.color === "תיקון 190");
  const hasPreReform = rows.some(r => r.color === "נתון לבחירה");
  if (fund.cat === "קופת גמל" && fund.tikun190 === "כן" && kitzbaSum > 0 && !hasHon && !hasPreReform) {
    for (const r of rows) {
      if (r.color === "תגמולים קצבה") { r.color = "תיקון 190"; r._movedFromKitzba = true; }
    }
  }

  return rows;
}

export function aggregateByColor(rows) {
  const map = new Map();
  for (const r of rows) {
    map.set(r.color, (map.get(r.color) || 0) + r.amount);
  }
  return map;
}

// מילות "רעש" בשמות מעסיקים ישראליים — סיומת תאגידית/מדינה שמופיעה לא-אחיד
// בין קבצי מסלקה שונים (יש/אין, עם/בלי גרשיים) ומטשטשת התאמה של אותו מעסיק
// עצמו. מכוונת — לא כוללת מילים תוכניות (כמו שם החברה עצמו), רק boilerplate.
const NOISE_WORDS = new Set(["ישראל", "בעמ"]);

// מפתח קיבוץ בלבד — לא לתצוגה. מסיר גרשיים/גרשים (וריאציות כתיב), מחליף
// סוגריים/פיסוק ברווח, מסיר מילות-רעש כמילה שלמה, ואז מאחד את שאר האותיות
// בלי רווחים — כדי שהבדלי רווח בתוך השם עצמו (למשל "סמית קליין" מול
// "סמיתקליין") לא ייצרו שני מעסיקים נפרדים.
export function normalizeEmployerKey(name) {
  if (!name) return "";
  const words = name
    .replace(/["'׳״´`]/g, "")
    .replace(/[().,]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter(w => !NOISE_WORDS.has(w));
  return words.join("").toLowerCase();
}

/**
 * מקבץ לפי מעסיק — עם נירמול שמות זהיר (רווחים/פיסוק/סיומת תאגידית) כדי
 * שאותו מעסיק אמיתי לא יתפצל לכמה שורות בגלל כתיב לא-אחיד במסלקה. השם
 * המוצג הוא הווריאציה הראשונה שנתקלנו בה; שאר הווריאציות (אם יש) נשמרות
 * ב-rawNames לשקיפות — כדי שאפשר יהיה לוודא שהאיחוד נכון ולא להסתיר מידע.
 * @returns {Array<{employer, rawNames: string[], total, byColor: Map<color, amount>}>}
 */
export function aggregateByEmployer(rows) {
  const map = new Map();
  for (const r of rows) {
    const label = r.employer || "לא ידוע";
    const key = label === "לא ידוע" ? label : normalizeEmployerKey(label);
    if (!map.has(key)) map.set(key, { employer: label, rawNames: new Set([label]), total: 0, byColor: new Map() });
    const entry = map.get(key);
    entry.rawNames.add(label);
    entry.total += r.amount;
    entry.byColor.set(r.color, (entry.byColor.get(r.color) || 0) + r.amount);
  }
  return Array.from(map.values())
    .map(e => ({ ...e, rawNames: Array.from(e.rawNames) }))
    .sort((a, b) => b.total - a.total);
}
